import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../../components/PageHeader';
import { DataTable } from '../../../components/DataTable';
import { EmployeePicker } from '../../../components/EmployeePicker';
import { useToast } from '../../../components/Toast';
import { usePermission } from '../../../auth/AuthContext';
import { authorityApi, d10, todayIso, Row } from '../../../api/authority';
import { AuthorityNav, Pill, errMsg } from './authorityUi';

function Tree({ nodes, parent, depth }: { nodes: Row[]; parent: number | null; depth: number }) {
  const children = nodes.filter((n) => (n.reports_to_position_id ?? null) === parent && n.status === 'active');
  if (!children.length || depth > 12) return null;
  return (
    <ul className="org-tree">
      {children.map((n) => (
        <li key={n.id}>
          <Link to={`/admin/authority/positions/${n.id}`}>{n.title}</Link>{' '}
          {Number(n.holder_count) === 0 ? <Pill tone="warn">Vacant</Pill> : <span className="muted">({n.holder_count})</span>}
          {n.authority_level !== null && <span className="muted"> · A{n.authority_level}</span>}
          <Tree nodes={nodes} parent={n.id} depth={depth + 1} />
        </li>
      ))}
    </ul>
  );
}

export function ReportingStructurePage() {
  const canManage = usePermission('admin.authority.manage');
  const qc = useQueryClient(); const { showSuccess, showError } = useToast();
  const positions = useQuery({ queryKey: ['authority-positions'], queryFn: () => authorityApi.positions() });
  const exceptions = useQuery({ queryKey: ['authority-sup-exc'], queryFn: () => authorityApi.supervisorExceptions() });
  const [f, setF] = useState<Row>({ employeeId: '', supervisorEmployeeId: '', effectiveFrom: todayIso(), effectiveUntil: '', reason: '' });
  const add = useMutation({ mutationFn: () => authorityApi.addSupervisorException({ employeeId: Number(f.employeeId), supervisorEmployeeId: Number(f.supervisorEmployeeId), effectiveFrom: f.effectiveFrom, effectiveUntil: f.effectiveUntil || null, reason: f.reason }),
    onSuccess: () => { showSuccess('Supervisor exception recorded.'); setF({ ...f, employeeId: '', supervisorEmployeeId: '', reason: '' }); qc.invalidateQueries({ queryKey: ['authority-sup-exc'] }); }, onError: (e) => showError(errMsg(e, 'Failed.')) });
  const end = useMutation({ mutationFn: (id: number) => authorityApi.endSupervisorException(id, todayIso()), onSuccess: () => qc.invalidateQueries({ queryKey: ['authority-sup-exc'] }), onError: (e) => showError(errMsg(e, 'Failed.')) });
  const nodes = positions.data?.data ?? [];
  return (
    <div className="page">
      <PageHeader title="Reporting Structure" description="Who reports to whom. Supervisors are resolved from this structure; it never creates extra approval steps on its own." />
      <AuthorityNav current="/admin/authority/reporting" />
      <div className="card"><h2>Organisation Structure</h2>{nodes.length ? <Tree nodes={nodes} parent={null} depth={0} /> : <p className="muted">No positions yet.</p>}</div>
      <div className="card">
        <h2>Supervisor Exceptions</h2>
        <p className="muted">Use only when an employee's supervisor must differ from their position's reporting line (e.g. several people hold the position they report to). Dated, reasoned and audited.</p>
        <DataTable rows={exceptions.data?.data ?? []} rowKey={(r) => r.id} emptyMessage="No exceptions - all supervisors come from the reporting structure." columns={[
          { header: 'Employee', render: (r) => r.employee_name }, { header: 'Supervisor', render: (r) => r.supervisor_name },
          { header: 'From', render: (r) => d10(r.effective_from) }, { header: 'Until', render: (r) => d10(r.effective_until) },
          { header: 'Reason', render: (r) => r.reason }, { header: '', render: (r) => (canManage && r.is_current ? <button className="link-button" onClick={() => end.mutate(r.id)}>End today</button> : r.is_current ? <Pill tone="ok">Current</Pill> : <Pill tone="muted">Ended</Pill>) },
        ]} />
        {canManage && (
          <form className="inline-form" style={{ marginTop: 12 }} onSubmit={(e) => { e.preventDefault(); add.mutate(); }}>
            <label>Employee<EmployeePicker value={f.employeeId} onChange={(e) => setF({ ...f, employeeId: e.target.value })} required /></label>
            <label>Supervisor<EmployeePicker value={f.supervisorEmployeeId} onChange={(e) => setF({ ...f, supervisorEmployeeId: e.target.value })} required excludeId={Number(f.employeeId) || undefined} /></label>
            <label>From<input type="date" required value={f.effectiveFrom} onChange={(e) => setF({ ...f, effectiveFrom: e.target.value })} /></label>
            <label>Until<input type="date" value={f.effectiveUntil} onChange={(e) => setF({ ...f, effectiveUntil: e.target.value })} /></label>
            <label style={{ flex: 2 }}>Reason<input required minLength={5} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></label>
            <button type="submit" disabled={add.isPending}>Record Exception</button>
          </form>
        )}
      </div>
    </div>
  );
}
